/**
 * /c/:slug/meetings - Cabinet meetings tab.
 *
 * Per CHAP-230 (Phase 1Q #206). List + detail pattern; transcript text
 * gated on redaction_status='redacted_publishable' for Cabinet viewers
 * per CHAP-237 enforcement rule.
 *
 * Admin/team see all statuses + redaction flip controls + redaction
 * status badge per row.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch } from '../lib/api.js';

interface MeetingRow {
  id: string;
  title: string;
  date: string;
  durationMin: number | null;
  summaryMd: string | null;
  redactionStatus: 'raw' | 'in_review' | 'redacted_publishable';
  transcriptQuality: 'good' | 'partial' | 'garbage';
  provenanceTier: string;
  hasRecording: boolean;
  hasTranscript: boolean;
  updatedAt: string | null;
}

interface MeetingsListResponse {
  canManage: boolean;
  meetings: MeetingRow[];
}

interface AttendeeRow {
  tenantUserId: string;
  attended: boolean;
  name: string | null;
  email: string;
}

interface ActionRow {
  id: string;
  title: string;
  ownerUserId: string | null;
  ownerName: string | null;
  dueDate: string | null;
  status: string | null;
  sourceQuote: string | null;
  createdAt: string | null;
}

interface MeetingDetailResponse {
  canManage: boolean;
  meeting: MeetingRow & {
    // CHAP-308: the server no longer returns raw R2 keys. These say whether
    // the media exists and whether this viewer may fetch it; the bytes come
    // from a short-lived signed URL minted by /media/:kind on demand.
    hasRecording: boolean;
    hasTranscript: boolean;
    canDownloadMedia: boolean;
    transcriptHidden: boolean;
    hqUrl: string | null;
  };
  attendees: AttendeeRow[];
  actions: ActionRow[];
}

const REDACTION_BADGE: Record<MeetingRow['redactionStatus'], { label: string; cls: string }> = {
  raw: { label: 'Raw', cls: 'bg-red-100 text-red-800' },
  in_review: { label: 'In review', cls: 'bg-amber-100 text-amber-900' },
  redacted_publishable: { label: 'Publishable', cls: 'bg-green-100 text-green-800' },
};

const QUALITY_BADGE: Record<MeetingRow['transcriptQuality'], { label: string; cls: string }> = {
  good: { label: 'Good', cls: 'bg-green-50 text-green-700' },
  partial: { label: 'Partial', cls: 'bg-amber-50 text-amber-700' },
  garbage: { label: 'Garbage', cls: 'bg-red-50 text-red-700' },
};

export default function Meetings() {
  const { slug } = useParams<{ slug: string }>();
  const tenantSlug = slug ?? '';
  const [activeId, setActiveId] = useState<string | null>(null);

  const listQ = useQuery<MeetingsListResponse, Error>({
    queryKey: ['meetings', tenantSlug],
    queryFn: () => apiGet<MeetingsListResponse>(`/api/c/${tenantSlug}/meetings`),
  });

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">Meetings</h2>
        <p className="text-neutral text-sm mt-1">
          Cabinet + committee meetings. Recordings stay in the data room;
          transcripts surface here only after admin redaction review.
        </p>
      </header>

      {listQ.error && listQ.error.message.includes('403') && (
        <div className="border border-amber-200 bg-amber-50 text-amber-900 text-sm rounded-xl p-4">
          The Meetings tab is visible to Cabinet members + staff. Ask a Cabinet
          admin to grant access if you need it.
        </div>
      )}
      {listQ.error && !listQ.error.message.includes('403') && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
          Failed to load meetings: {listQ.error.message}
        </div>
      )}

      {listQ.data && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-4 min-w-0">
          {/* List */}
          <section className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden min-w-0">
            <header className="px-3 sm:px-4 py-2 border-b border-gray-100 text-xs text-neutral flex items-center justify-between gap-2">
              <span>{listQ.data.meetings.length} meeting{listQ.data.meetings.length === 1 ? '' : 's'}</span>
              {listQ.data.canManage && <span className="text-[10px] uppercase tracking-wider text-orange-700">staff view</span>}
            </header>
            {listQ.data.meetings.length === 0 ? (
              <p className="p-6 text-center text-neutral text-sm">
                No meetings recorded yet.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {listQ.data.meetings.map((m) => {
                  const badge = REDACTION_BADGE[m.redactionStatus];
                  const isActive = m.id === activeId;
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(m.id)}
                        className={`w-full text-left p-3 sm:p-4 hover:bg-gray-50 min-w-0 ${isActive ? 'bg-gray-50' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2 min-w-0">
                          <div className="flex-1 min-w-0">
                            <p className="text-navy text-sm font-semibold truncate">{m.title}</p>
                            <p className="text-neutral text-xs mt-0.5">
                              {m.date}
                              {m.durationMin ? ` · ${m.durationMin} min` : ''}
                            </p>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.cls} flex-shrink-0`}>
                            {badge.label}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Detail */}
          <section className="lg:col-span-3 min-w-0">
            {!activeId && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
                Pick a meeting on the left to see attendees, actions, and (if
                published) the transcript summary.
              </div>
            )}
            {activeId && (
              <MeetingDetail tenantSlug={tenantSlug} meetingId={activeId} />
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function MeetingDetail({ tenantSlug, meetingId }: { tenantSlug: string; meetingId: string }) {
  const qc = useQueryClient();
  const q = useQuery<MeetingDetailResponse, Error>({
    queryKey: ['meeting', tenantSlug, meetingId],
    queryFn: () => apiGet<MeetingDetailResponse>(`/api/c/${tenantSlug}/meetings/${meetingId}`),
  });

  // CHAP-308: fetch a short-lived signed URL at click time rather than holding
  // one in state. A URL minted on page load would routinely be expired by the
  // time anyone clicked it, and it would sit in memory for the whole session.
  const [mediaError, setMediaError] = useState<string | null>(null);
  const media = useMutation({
    mutationFn: (kind: 'recording' | 'transcript') =>
      apiGet<{ url: string; kind: string; expiresAt: string }>(
        `/api/c/${tenantSlug}/meetings/${meetingId}/media/${kind}`,
      ),
    onSuccess: (r) => {
      setMediaError(null);
      window.location.assign(r.url);
    },
    onError: (e: Error) => setMediaError(e.message || 'Could not open that file.'),
  });

  const flip = useMutation({
    mutationFn: (body: { redactionStatus: MeetingRow['redactionStatus']; transcriptQuality?: MeetingRow['transcriptQuality'] }) =>
      apiPatch<{ id: string }>(`/api/c/${tenantSlug}/meetings/${meetingId}/redaction`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meeting', tenantSlug, meetingId] });
      qc.invalidateQueries({ queryKey: ['meetings', tenantSlug] });
    },
  });

  const addAction = useMutation({
    mutationFn: (body: { title: string }) =>
      apiPost<{ id: string }>(`/api/c/${tenantSlug}/meetings/${meetingId}/actions`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meeting', tenantSlug, meetingId] }),
  });

  if (q.isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
        Loading…
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
        Failed to load: {q.error.message}
      </div>
    );
  }
  if (!q.data) return null;
  const { meeting, attendees, actions, canManage } = q.data;
  const badge = REDACTION_BADGE[meeting.redactionStatus];
  const quality = QUALITY_BADGE[meeting.transcriptQuality];

  return (
    <div className="space-y-3 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-navy text-base sm:text-lg font-bold">{meeting.title}</h3>
            <p className="text-neutral text-xs mt-0.5">
              {meeting.date}
              {meeting.durationMin ? ` · ${meeting.durationMin} min` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${quality.cls}`}>{quality.label}</span>
          </div>
        </div>
        {canManage && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-neutral">Redaction:</span>
            {(['raw', 'in_review', 'redacted_publishable'] as const).map((s) => (
              <button
                key={s}
                type="button"
                disabled={flip.isPending || meeting.redactionStatus === s}
                onClick={() => flip.mutate({ redactionStatus: s })}
                className={`text-xs px-2 py-1 rounded border ${
                  meeting.redactionStatus === s
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-navy border-gray-300 hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                {REDACTION_BADGE[s].label}
              </button>
            ))}
            <span className="text-[10px] uppercase tracking-wider text-neutral ml-3">Quality:</span>
            {(['good', 'partial', 'garbage'] as const).map((q) => (
              <button
                key={q}
                type="button"
                disabled={flip.isPending || meeting.transcriptQuality === q}
                onClick={() => flip.mutate({ redactionStatus: meeting.redactionStatus, transcriptQuality: q })}
                className={`text-xs px-2 py-1 rounded border ${
                  meeting.transcriptQuality === q
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-navy border-gray-300 hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                {QUALITY_BADGE[q].label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Summary */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 min-w-0">
        <p className="text-neutral text-[10px] uppercase tracking-wider mb-2">Summary</p>
        {meeting.summaryMd ? (
          <pre className="text-sm text-navy whitespace-pre-wrap font-sans leading-relaxed">{meeting.summaryMd}</pre>
        ) : meeting.transcriptHidden ? (
          <p className="text-sm text-neutral italic">
            Transcript hidden until admin promotes redaction status to publishable.
          </p>
        ) : (
          <p className="text-sm text-neutral italic">No summary captured.</p>
        )}
      </section>

      {/* Recording + transcript (CHAP-308). Rendered whenever media exists, so
          a Cabinet member is told an unreleased transcript is there rather than
          being shown nothing at all. */}
      {(meeting.hasRecording || meeting.hasTranscript) && (
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 min-w-0">
          <p className="text-neutral text-[10px] uppercase tracking-wider mb-2">
            Recording &amp; transcript
          </p>
          {meeting.canDownloadMedia ? (
            <>
              <div className="flex flex-wrap gap-2">
                {meeting.hasRecording && (
                  <button
                    type="button"
                    onClick={() => media.mutate('recording')}
                    disabled={media.isPending}
                    className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 text-navy hover:border-navy disabled:opacity-50"
                  >
                    {media.isPending && media.variables === 'recording'
                      ? 'Preparing…'
                      : 'Download recording'}
                  </button>
                )}
                {meeting.hasTranscript && (
                  <button
                    type="button"
                    onClick={() => media.mutate('transcript')}
                    disabled={media.isPending}
                    className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 text-navy hover:border-navy disabled:opacity-50"
                  >
                    {media.isPending && media.variables === 'transcript'
                      ? 'Preparing…'
                      : 'Download transcript'}
                  </button>
                )}
              </div>
              <p className="text-[11px] text-neutral mt-2">
                Links are generated per click and expire shortly after.
              </p>
              {mediaError && (
                <p className="text-[11px] text-red-700 mt-2">{mediaError}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-neutral italic">
              {meeting.hasRecording && meeting.hasTranscript
                ? 'A recording and transcript exist but have not been released yet.'
                : meeting.hasRecording
                  ? 'A recording exists but has not been released yet.'
                  : 'A transcript exists but has not been released yet.'}
            </p>
          )}
        </section>
      )}

      {/* Attendees */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 min-w-0">
        <p className="text-neutral text-[10px] uppercase tracking-wider mb-2">
          Attendees ({attendees.length})
        </p>
        {attendees.length === 0 ? (
          <p className="text-sm text-neutral italic">No attendees recorded.</p>
        ) : (
          <ul className="space-y-1">
            {attendees.map((a) => (
              <li key={a.tenantUserId} className="flex items-center gap-2 text-sm min-w-0">
                <span className={`w-1.5 h-1.5 rounded-full ${a.attended ? 'bg-green-500' : 'bg-gray-300'} flex-shrink-0`} />
                <span className="text-navy truncate">{a.name ?? a.email}</span>
                {!a.attended && <span className="text-[10px] text-neutral italic">declined</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Actions */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <p className="text-neutral text-[10px] uppercase tracking-wider">
            Actions ({actions.length})
          </p>
          {canManage && (
            <button
              type="button"
              onClick={() => {
                const title = window.prompt('Action item title?');
                if (title && title.trim().length > 0) {
                  addAction.mutate({ title: title.trim() });
                }
              }}
              className="text-xs font-medium px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
            >
              + Add action
            </button>
          )}
        </div>
        {actions.length === 0 ? (
          <p className="text-sm text-neutral italic">No actions captured.</p>
        ) : (
          <ul className="space-y-2">
            {actions.map((a) => (
              <li key={a.id} className="border-l-2 border-orange/40 pl-3 py-1 min-w-0">
                <p className="text-sm text-navy">{a.title}</p>
                <p className="text-xs text-neutral mt-0.5">
                  {a.ownerName ?? 'unassigned'}
                  {a.dueDate ? ` · due ${a.dueDate}` : ''}
                  {a.status ? ` · ${a.status}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
